import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import theme from '../../../../styles/theme';
import { 
  Card, 
  Input,
  PrimaryButton
} from '../../../../styles/components';
import { glassDeep, glassMedium } from '../../../../styles/components/common/glassmorphism';
import { fadeInAnimation } from '../../../../styles/components/common/animations';

const ChatContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  ${glassDeep}
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.lg};
  }
`;

const ChatHeader = styled.div`
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  background: rgba(15, 23, 42, 0.3);
  ${glassMedium}
  
  h3 {
    margin: 0;
    font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.md};
    color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
    transition: color 0.3s ease;
    
    &:hover {
      color: ${({ theme: propTheme }) => (propTheme || theme).colors.primaryLight};
    }
  }
`;

const OnlineCounter = styled.span`
  background: rgba(16, 185, 129, 0.2);
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.success};
  font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.xs};
  padding: ${({ theme: propTheme }) => `${(propTheme || theme).spacing.xs} ${(propTheme || theme).spacing.sm}`};
  border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.pill};
  display: flex;
  align-items: center;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.xs};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(16, 185, 129, 0.3);
  }
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${({ theme: propTheme }) => (propTheme || theme).colors.success};
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      opacity: 0.5;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
    100% {
      opacity: 0.5;
      transform: scale(0.8);
    }
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.xs};
  animation: ${fadeInAnimation} 0.3s ease-out;
  
  .header {
    display: flex;
    align-items: center;
    gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
    
    .name {
      font-weight: ${({ theme: propTheme }) => (propTheme || theme).typography.fontWeight.medium};
      color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
      transition: color 0.3s ease;
      
      &:hover {
        color: ${({ theme: propTheme }) => (propTheme || theme).colors.primaryLight};
      }
    }
    
    .time {
      color: rgba(255, 255, 255, 0.6);
      font-size: ${({ theme: propTheme }) => (propTheme || theme).typography.fontSize.xs};
    }
  }
  
  .content {
    color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
    line-height: ${({ theme: propTheme }) => (propTheme || theme).typography.lineHeight.normal};
    background: rgba(255, 255, 255, 0.05);
    padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
    border-radius: ${({ theme: propTheme }) => (propTheme || theme).borderRadius.md};
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
  }
`;

const InputContainer = styled.div`
  padding: ${({ theme: propTheme }) => (propTheme || theme).spacing.md};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: ${({ theme: propTheme }) => (propTheme || theme).spacing.sm};
  background: rgba(15, 23, 42, 0.3);
  ${glassMedium}
`;

const StyledInput = styled(Input)`
  background: rgba(15, 23, 42, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: ${({ theme: propTheme }) => (propTheme || theme).colors.textOnDark};
  transition: all 0.3s ease;
  
  &:focus {
    border-color: ${({ theme: propTheme }) => (propTheme || theme).colors.primary};
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SendButton = styled(PrimaryButton)`
  white-space: nowrap;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${({ theme: propTheme }) => (propTheme || theme).shadows.md};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
  }
`;

// Mock data for demonstration
const mockMessages = [
  { id: 1, user: 'Alice Chen', content: 'Great session! Looking forward to learning more.', time: '2:30 PM' },
  { id: 2, user: 'Mohammed Ali', content: 'Could you explain the previous slide in more detail?', time: '2:32 PM' },
  { id: 3, user: 'Sarah Johnson', content: 'The practical example really helped clarify things.', time: '2:35 PM' },
];

const LiveChat = ({ onlineCount }) => {
  const [messages, setMessages] = useState(mockMessages);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = () => {
    if (!newMessage.trim()) return;
    
    setMessages([
      ...messages,
      {
        id: messages.length + 1,
        user: 'You',
        content: newMessage.trim(),
        time: new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: 'numeric',
        }).format(new Date()),
      },
    ]);
    setNewMessage('');
  };

  return (
    <ChatContainer>
      <ChatHeader>
        <h3>Live Chat</h3>
        <OnlineCounter>{onlineCount} online</OnlineCounter>
      </ChatHeader>
      
      <MessagesContainer>
        {messages.map((message) => (
          <Message key={message.id}>
            <div className="header">
              <span className="name">{message.user}</span>
              <span className="time">{message.time}</span>
            </div>
            <div className="content">{message.content}</div>
          </Message>
        ))}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      
      <InputContainer>
        <StyledInput
          type="text"
          placeholder="Type your message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
        />
        <SendButton onClick={handleSend} disabled={!newMessage.trim()}>
          Send
        </SendButton>
      </InputContainer>
    </ChatContainer>
  );
};

export default LiveChat;
